/**
 * Small dependency-free SVG charts for the progress screen. One series per chart, thin
 * marks, recessive grid, hover tooltip, and a table fallback via <title> elements.
 */
import { h } from '../dom.js';

const NS = 'http://www.w3.org/2000/svg';
function svgEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

export interface Point {
  x: number;
  y: number;
  label: string;
}

export function lineChart(points: Point[], opts: { colour: string; yMin?: number; yMax?: number; format(v: number): string; title: string; empty?: string }): HTMLElement {
  const W = 640;
  const H = 200;
  const P = { l: 48, r: 12, t: 12, b: 26 };
  const wrap = h('div', { class: 'chart-wrap' });
  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, class: 'chart', role: 'img', 'aria-label': opts.title });
  wrap.append(h('div', { class: 'chart-title' }, opts.title), svg);
  if (points.length === 0) {
    wrap.append(h('div', { class: 'muted small' }, opts.empty ?? 'No data yet.'));
    return wrap;
  }
  const ys = points.map((p) => p.y);
  const yMin = opts.yMin ?? Math.floor(Math.min(...ys) * 0.9);
  const yMax = opts.yMax ?? Math.max(yMin + 1, Math.ceil(Math.max(...ys) * 1.1));
  const n = points.length;
  const sx = (i: number) => P.l + (n === 1 ? (W - P.l - P.r) / 2 : (i / (n - 1)) * (W - P.l - P.r));
  const sy = (v: number) => P.t + (1 - (v - yMin) / Math.max(1e-9, yMax - yMin)) * (H - P.t - P.b);
  for (let g = 0; g <= 4; g++) {
    const v = yMin + ((yMax - yMin) * g) / 4;
    svg.append(svgEl('line', { x1: P.l, x2: W - P.r, y1: sy(v), y2: sy(v), class: 'grid' }));
    const t = svgEl('text', { x: P.l - 6, y: sy(v) + 4, class: 'axis', 'text-anchor': 'end' });
    t.textContent = opts.format(v);
    svg.append(t);
  }
  const xl = svgEl('text', { x: P.l, y: H - 6, class: 'axis' });
  xl.textContent = 'oldest';
  const xr = svgEl('text', { x: W - P.r, y: H - 6, class: 'axis', 'text-anchor': 'end' });
  xr.textContent = 'latest';
  svg.append(xl, xr);
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${sx(i).toFixed(1)},${sy(p.y).toFixed(1)}`).join(' ');
  svg.append(svgEl('path', { d, fill: 'none', stroke: opts.colour, 'stroke-width': 2, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }));
  const tip = h('div', { class: 'chart-tip', hidden: true });
  wrap.append(tip);
  points.forEach((p, i) => {
    const c = svgEl('circle', { cx: sx(i), cy: sy(p.y), r: 4, fill: opts.colour, stroke: '#fff', 'stroke-width': 2 });
    const hit = svgEl('circle', { cx: sx(i), cy: sy(p.y), r: 12, fill: 'transparent' });
    const title = svgEl('title', {});
    title.textContent = p.label;
    hit.append(title);
    hit.addEventListener('mouseenter', () => {
      tip.textContent = p.label;
      tip.hidden = false;
      tip.style.left = `${(sx(i) / W) * 100}%`;
    });
    hit.addEventListener('mouseleave', () => (tip.hidden = true));
    svg.append(c, hit);
  });
  return wrap;
}

export function barChart(rows: { label: string; value: number; detail: string }[], opts: { colour: string; title: string; format(v: number): string; max?: number }): HTMLElement {
  const wrap = h('div', { class: 'chart-wrap' }, h('div', { class: 'chart-title' }, opts.title));
  if (rows.length === 0) {
    wrap.append(h('div', { class: 'muted small' }, 'Nothing to show yet.'));
    return wrap;
  }
  const max = opts.max ?? Math.max(...rows.map((r) => r.value), 1e-9);
  const list = h('div', { class: 'bars' });
  for (const r of rows) {
    list.append(
      h(
        'div',
        { class: 'bar-row', title: r.detail },
        h('span', { class: 'bar-label' }, r.label),
        h('div', { class: 'bar-track' }, h('div', { class: 'bar-fill', style: `width:${((r.value / max) * 100).toFixed(1)}%;background:${opts.colour}` })),
        h('span', { class: 'bar-value' }, opts.format(r.value)),
      ),
    );
  }
  wrap.append(list);
  return wrap;
}
